// Google Drive picker download-and-stage phase (Flow 5 of the native
// background-upload feature). Runs identically in the visible app (via
// stagingProvider.js/@capacitor/filesystem) and in the headless WebView
// FolderSyncForegroundService spins up to resume an interrupted job (via
// window.cmHeadlessSyncBridge - a bare WebView has no Capacitor Bridge) -
// same headlessBridge() detection idiom as nativeProvider.js.
//
// Deliberately does NOT compress anything here - it only fetches and stages
// the RAW bytes Drive returns. Compression happens exactly once, downstream,
// inside the same runOrganiserUploadBatches engine every other upload flow
// already uses (see EventPage.js) - staging raw bytes here
// means a resumed download never has to guess whether a partially-processed
// file was already compressed.
import * as stagingProvider from './stagingProvider';

function headlessBridge() {
  return typeof window !== 'undefined' ? window.cmHeadlessSyncBridge : undefined;
}

export async function downloadDriveFile(fileId, fileName, accessToken) {
  const url = new URL(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}`);
  url.searchParams.set('alt', 'media');
  url.searchParams.set('supportsAllDrives', 'true');

  const resp = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!resp.ok) {
    const body = await resp.text();
    throw new Error(`Failed to download ${fileName}: ${resp.status} ${body}`);
  }
  const blob = await resp.blob();
  return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
}

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.onload = () => {
      const commaIndex = String(reader.result).indexOf(',');
      resolve(commaIndex >= 0 ? reader.result.slice(commaIndex + 1) : reader.result);
    };
    reader.readAsDataURL(blob);
  });
}

async function loadHeadlessDriveDownload(jobId, entry) {
  const meta = JSON.parse(headlessBridge().readDriveDownload(jobId, entry.index));
  if (meta.error) throw new Error(meta.error);
  const res = await fetch(`data:image/jpeg;base64,${meta.base64}`);
  const blob = await res.blob();
  return new File([blob], entry.name, { type: blob.type || 'image/jpeg' });
}

// Downloads whatever `manifest.driveFiles` entries aren't yet `downloaded`,
// staging each one as it completes so a subsequent interruption only re-does
// the remainder. `onProgress(completed, total)` fires after each file.
// `shouldCancel()`, if given, is checked before starting each file's download -
// same per-file (not mid-single-file-transfer) granularity CmUploadWorker's own
// isCancelled() checks use between files in a batch. Throws the same
// 'Upload cancelled.' error resumeUploadJob.js's other cancellation points use,
// so its outcome classification treats this the same way.
export async function downloadAndStageDriveFiles(jobId, manifest, accessToken, onProgress, shouldCancel) {
  const bridge = headlessBridge();
  const pending = manifest.driveFiles.filter((f) => !f.downloaded);
  let completed = manifest.driveFiles.length - pending.length;
  for (const entry of pending) {
    if (shouldCancel?.()) throw new Error('Upload cancelled.');
    const file = await downloadDriveFile(entry.driveFileId, entry.name, accessToken);
    if (bridge) {
      const base64 = await blobToBase64(file);
      bridge.writeDriveDownload(jobId, entry.index, base64);
      bridge.markDriveDownloaded(jobId, entry.index);
    } else {
      await stagingProvider.stageDriveDownload(jobId, entry.index, file);
      await stagingProvider.markDriveDownloaded(jobId, entry.index);
    }
    completed += 1;
    onProgress?.(completed, manifest.driveFiles.length);
  }
}

// Loads every downloaded drive file back as a plain {file, relativePath}
// pair, ready to feed into runOrganiserUploadBatches exactly like any
// local-file batch - that engine does its own single compression pass over
// whatever `file` they're given, so nothing here is pre-compressed.
export async function rebuildDownloadedDriveFiles(jobId, manifest) {
  const bridge = headlessBridge();
  const entries = [];
  for (const entry of manifest.driveFiles) {
    if (!entry.downloaded) continue;
    const file = bridge
      ? await loadHeadlessDriveDownload(jobId, entry)
      : await stagingProvider.loadStagedDriveDownload(jobId, entry);
    entries.push({ file, relativePath: entry.name });
  }
  return entries;
}
